/* eslint-disable react/jsx-no-target-blank */
import React from "react";
import { CompanyLogoOutlined, ArrowRightShape } from "@themuse/design-system";
import cn from "classnames";

import { IndustryIcon } from "components/icons/IndustryIcon";
import { SizeIcon } from "components/icons/SizeIcon";
import { CompanyIndustry, SingleCompany, SingleJob } from "interfaces/api/apiInterfaces";
import { formatCompanySize, getCompanyIndustries, getCompanyLogo } from "utils/strings";
import {
    museTrack, getJobTrackingProps, JobTrackingProps, LinkClickedTrackingProps
} from "utils/tracking";

import styles from "./CompanyInformationModule.module.scss";

const customLinkClickedTracking = (
    event: React.SyntheticEvent<HTMLAnchorElement, Event>,
    trackingProps: JobTrackingProps & LinkClickedTrackingProps
) => {
    museTrack("Jobs Individual Link Clicked", {
        ...trackingProps,
        ...{
            link: event.currentTarget.href,
        }
    });
};

const getPilboxUrl = (imageUrl: string, imageWidth: number): string => {
    const pilboxUrl = "https://pilbox.themuse.com/";
    const imageUrlRegex = /^https?:\/\//;

    if (imageUrlRegex.test(imageUrl)) {
        return `${pilboxUrl}?url=${imageUrl}&w=${imageWidth}&filter=antialias&opt=1&pos=center&prog=1&q=keep`;
    }

    return "";
};

interface ICompanyInformationModule {
    company: SingleCompany;
    job: SingleJob;
    displayOnMobile?: boolean;
}

/**
 * Generates the company logo
 */
const CompanyLogo = (props: { url: string, name: string }): React.ReactElement | null => {
    const { url = "", name = "" } = props;
    return url
        ? (
            <CompanyLogoOutlined
                className={styles.companyLogo}
                logoUrl={url}
                altText={`${name} Logo`}
            />
        ) : null;
};

/**
 * Generates Heading
 */
const CompanyHeading = (props: { desc: string }): React.ReactElement | null => {
    const { desc = "" } = props;
    return desc.length > 10
        ? (
            <>
                <h2>About Us</h2>
                <p className={styles.companyShortDescription}>{desc}</p>
            </>
        )
        : null;
};

/**
 * Generates the range list items
 */
const CompanyRange = (props: { range: string, size: string }): React.ReactElement | null => {
    const { range = [], size = "" } = props;
    return range
        ? (
            <li>
                <SizeIcon /><strong>Size:</strong> {`${size} employees`}
            </li>
        )
        : null;
};

/**
 * Generates the industries list items
 */
const CompanyIndustries = (props: { industries: CompanyIndustry[], companyIndustries: string }): React.ReactElement | null => {
    const { industries = [], companyIndustries = "" } = props;
    return industries.length
        ? (
            <li>
                <IndustryIcon /><strong>Industry:</strong> {companyIndustries}
            </li>
        )
        : null;
};

/**
 * Generates the photo in the body of the module
 */
const CompanySnapshot = (props: { companyName: string, src: string, mobile: boolean }): React.ReactElement | null => {
    const { companyName = "", src = "", mobile = false } = props;
    const loading = mobile ? "lazy" : "eager";
    return src
        ? (
            <img alt={companyName} loading={loading} src={src} height="167" width="250" />
        )
        : null;
};

/**
 * Generates the Link at the bottom of the module
 */
const CompanyProfileLink = (props: { companyName: string, tracking: JobTrackingProps }): React.ReactElement | null => {
    const { companyName, tracking } = props;
    const companyProfileLinkClickedTrackingProps = {
        ...tracking,
        ...{
            click_origin: "company_info_module",
            new_tab: true
        }
    };
    return (
        <a
            className={styles.companyProfileLink}
            onClick={(event: React.SyntheticEvent<HTMLAnchorElement, Event>) => customLinkClickedTracking(event, companyProfileLinkClickedTrackingProps)}
            href={`/profiles/${companyName}`}
            target="_blank"
        >View Company Profile<ArrowRightShape />
        </a>
    );
};

export const CompanyInformationModule = (props: ICompanyInformationModule) => {
    const { company, job, displayOnMobile = false } = props;
    const companySize = formatCompanySize(company?.employee_range);
    const companyIndustries = getCompanyIndustries(company?.industries);
    const companyLogo = getCompanyLogo(company?.logo) || "";
    const jobTrackingProps = getJobTrackingProps(job, company);
    const snapshotPhotoSrc = getPilboxUrl(company?.snapshot_photo, 500);
    const display = displayOnMobile ? "mobile" : "desktop";
    const containerClasses = cn(
        snapshotPhotoSrc
            ? `${styles.companyInformationModule} ${styles.hasPhoto} ${styles[display]}`
            : `${styles.companyInformationModule} ${styles[display]}`
    );

    return (
        <aside className={containerClasses}>
            <CompanyLogo {...{ url: companyLogo, name: company?.name }} />
            <CompanyHeading {...{ desc: company.short_description }} />
            <ul>
                <CompanyRange {...{ range: company.employee_range, size: companySize }} />
                <CompanyIndustries {...{ industries: company.industries, companyIndustries }} />
            </ul>
            <CompanySnapshot {...{ companyName: company.name, src: snapshotPhotoSrc, mobile: displayOnMobile }} />
            <CompanyProfileLink {...{ companyName: company.short_name, tracking: jobTrackingProps }} />
        </aside>
    );
};
